import React, { useRef, useEffect } from 'react';
import { Monitor, X, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ScreenShareDisplayProps {
  stream: MediaStream | null;
  isLocal?: boolean;
  sharerName?: string;
  onClose?: () => void;
  className?: string;
}

export const ScreenShareDisplay: React.FC<ScreenShareDisplayProps> = ({
  stream,
  isLocal = false,
  sharerName,
  onClose,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [stream]);

  const toggleFullscreen = async () => {
    if (!videoRef.current) return;

    if (!document.fullscreenElement) {
      await videoRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!stream) return null;

  return (
    <div
      className={cn(
        'relative bg-black rounded-xl overflow-hidden border-2 border-primary/50 shadow-xl',
        className
      )}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Monitor className="w-4 h-4" />
          <span className="text-sm font-medium">
            {isLocal ? 'Your Screen' : `${sharerName || 'Host'}'s Screen`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/20"
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </Button>
          {isLocal && onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-red-500/50"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Video */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className="w-full h-full object-contain"
      />

      {/* Sharing indicator */}
      {isLocal && (
        <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
          </span>
          Sharing Screen
        </div>
      )}
    </div>
  );
};

export default ScreenShareDisplay;
